import { ActionIcon, Button, Loader, Text, TextInput, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, LayoutGrid, SquareStack, Trash } from "lucide-react";
import { useState } from "react";

import {
  getListAdCopiesQueryKey,
  useUpdateAdCopy,
} from "../../../api/generated/endpoints/generation/generation";
import type { AdCopy } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import classes from "./ad-copies.module.css";
import { CopyEditor } from "./CopyEditor";
import { COPY_GENERATING, CopyFailed } from "./CopyView";
import { draftChanged, draftOf, draftValid, elementsOf } from "./editing";
import { countsOf } from "./elements";
import { ReferencePicture } from "./ReferencePicture";

export interface TabCopyEditProps {
  teamId: string;
  brandId: string;
  copy: AdCopy;
  product: string;
  /** A duplicate or a delete runs: its buttons wait. */
  busy: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
}

/**
 * The Ad copy tab's copy (live's `edit-text-copies`, captures advanced-234/01 to 03): its
 * inspiration, its title ("Untitled"), Duplicate and Delete, its product, "Sections: n" and
 * "Elements: n"; then live's generating words, its failure, or live's editor; and Save, greyed
 * until the copy is valid and changed. Mount it per copy and status, as live resets it then.
 */
export function TabCopyEdit(props: TabCopyEditProps) {
  const { teamId, brandId, copy, product, busy, onDuplicate, onDelete } = props;
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(() => draftOf(copy));
  const [draft, setDraft] = useState(saved);
  const update = useUpdateAdCopy({
    mutation: {
      onSuccess: async (updated) => {
        await queryClient.invalidateQueries({ queryKey: getListAdCopiesQueryKey(teamId, brandId) });
        // Live resets the form to what was saved.
        const next = draftOf(updated);
        setSaved(next);
        setDraft(next);
      },
    },
  });
  const saving = update.isPending;
  const writing = copy.status === "in_progress";
  const { sections, elements } = countsOf(copy.text_elements);
  return (
    <div className={classes.tabCard}>
      <div className={classes.tabHeader}>
        <ReferencePicture teamId={teamId} brandId={brandId} reference={copy.reference} />
        <div className={classes.tabHeaderText}>
          <div className={classes.tabRowTop}>
            <TextInput
              style={{ flex: 1 }}
              aria-label="Title"
              placeholder="Untitled"
              value={draft.name}
              disabled={saving || busy}
              onChange={(event) => {
                const name = event.currentTarget.value;
                setDraft((current) => ({ ...current, name }));
              }}
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Duplicate"
              disabled={saving || busy}
              onClick={onDuplicate}
            >
              <Copy size={20} strokeWidth={1.5} aria-hidden />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Delete"
              disabled={saving || busy}
              onClick={onDelete}
            >
              <Trash size={20} strokeWidth={1.5} aria-hidden />
            </ActionIcon>
          </div>
          <div className={classes.badges}>
            <Tooltip label={product} disabled={product === ""}>
              <span className={classes.badge}>{product}</span>
            </Tooltip>
            <span className={classes.badge}>
              <LayoutGrid size={16} aria-hidden />
              {writing ? <Loader size={10} aria-hidden /> : `Sections: ${sections}`}
            </span>
            <span className={classes.badge}>
              <SquareStack size={16} aria-hidden />
              {writing ? <Loader size={10} aria-hidden /> : `Elements: ${elements}`}
            </span>
          </div>
        </div>
      </div>
      <div className={classes.paneBody}>
        {writing ? (
          <div className={classes.state}>
            <Loader size="md" aria-hidden />
            <Text>{COPY_GENERATING}</Text>
          </div>
        ) : null}
        {copy.status === "failed" ? <CopyFailed /> : null}
        {copy.status === "completed" ? (
          <CopyEditor
            sections={draft.sections}
            disabled={saving}
            onChange={(next) => setDraft((current) => ({ ...current, sections: next }))}
          />
        ) : null}
        {update.isError ? <ErrorAlert error={update.error} /> : null}
      </div>
      <div className={classes.tabFooter}>
        <Button
          loading={saving}
          disabled={!draftValid(draft) || !draftChanged(draft, saved)}
          onClick={() =>
            update.mutate({
              teamId,
              brandId,
              copyId: copy.id,
              data: { name: draft.name, text_elements: elementsOf(draft) },
            })
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
}
